<script setup lang="ts">
/**
 * CanonState — D3 da Proposta Lume, Fase 3.
 *
 * State canônico: Canon pessoal. O "seu livro de compreensão".
 * Substitui o "perfil de aprendizado" tradicional.
 *
 * Constitucional:
 * - Art II §2.5: Canon é do aprendiz.
 * - P-UX-08 (Dignified feedback): entries são reflexões, não scores.
 * - P-UX-02 (One thing at a time): uma entry por vez; sem gamificação.
 * - Princípio III: continuidade, não eventos.
 *
 * Acessibilidade:
 * - aria-label em cada entry
 * - Filtros via radiogroup
 * - Export como markdown (link que baixa)
 */
import { ref, computed } from "vue";
import { useCanonStore } from "../store";
import type { CanonEntryKind } from "../types";

interface Emits {
  (e: "close"): void;
}

const emit = defineEmits<Emits>();
const canon = useCanonStore();

const filter = ref<CanonEntryKind | "all">("all");
const showAddForm = ref(false);
const newText = ref("");
const newTopic = ref("");
const newKind = ref<CanonEntryKind>("understanding");

const filtered = computed(() => {
  if (filter.value === "all") return canon.sorted;
  return canon.sorted.filter((e) => e.kind === filter.value);
});

const kindOptions: { value: CanonEntryKind | "all"; label: string }[] = [
  { value: "all", label: "Tudo" },
  { value: "understanding", label: "Compreensões" },
  { value: "question", label: "Perguntas" },
  { value: "changed_mind", label: "Mudanças de ideia" },
  { value: "metaphor", label: "Metáforas" },
  { value: "curiosity", label: "Curiosidades" },
  { value: "vocabulary", label: "Vocabulário" },
];

const kindPrompts: Record<CanonEntryKind, string> = {
  understanding: "O que agora você entende?",
  question: "O que ainda te intriga?",
  changed_mind: "Sobre o que você mudou de ideia?",
  metaphor: "Que metáfora te ajuda a entender isso?",
  curiosity: "O que te surpreendeu hoje?",
  vocabulary: "Que termo novo você quer guardar?",
};

function onAdd() {
  if (!newText.value.trim()) return;
  canon.add({
    kind: newKind.value,
    text: newText.value,
    topic: newTopic.value.trim() || undefined,
  });
  newText.value = "";
  newTopic.value = "";
  showAddForm.value = false;
}

function onDelete(id: string) {
  if (confirm("Remover esta entrada do Canon?")) {
    canon.remove(id);
  }
}

function onExport() {
  const md = canon.exportMarkdown();
  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meu-canon-${new Date().toISOString().split("T")[0]}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("pt-BR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
</script>

<template>
  <section
    class="hlos-canon"
    role="region"
    aria-labelledby="hlos-canon-title"
    data-testid="canon-state"
  >
    <header class="hlos-canon__header">
      <h2 id="hlos-canon-title">Meu Canon</h2>
      <p class="hlos-canon__intro">
        Seu livro de compreensão. Cada entrada é uma reflexão sua — não há
        pontuação, não há ordem de classificação. Você pode exportar,
        editar ou apagar a qualquer momento.
      </p>
      <div class="hlos-canon__actions">
        <button
          type="button"
          class="hlos-canon__btn hlos-canon__btn--primary"
          data-testid="canon-add-toggle"
          @click="showAddForm = !showAddForm"
        >
          {{ showAddForm ? "Cancelar" : "Adicionar entrada" }}
        </button>
        <button
          type="button"
          class="hlos-canon__btn"
          :disabled="canon.entries.length === 0"
          data-testid="canon-export"
          @click="onExport"
        >
          Exportar
        </button>
        <button
          type="button"
          class="hlos-canon__btn hlos-canon__btn--tertiary"
          data-testid="canon-close"
          @click="emit('close')"
        >
          Voltar
        </button>
      </div>
    </header>

    <form
      v-if="showAddForm"
      class="hlos-canon__form"
      @submit.prevent="onAdd"
    >
      <label for="canon-kind" class="hlos-canon__label">Tipo de entrada</label>
      <select
        id="canon-kind"
        v-model="newKind"
        class="hlos-canon__select"
        data-testid="canon-new-kind"
      >
        <option
          v-for="opt in kindOptions.filter((o) => o.value !== 'all')"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
      <p class="hlos-canon__prompt">{{ kindPrompts[newKind] }}</p>
      <label for="canon-text" class="sr-only">Texto da entrada</label>
      <textarea
        id="canon-text"
        v-model="newText"
        rows="3"
        placeholder="Escreva aqui…"
        data-testid="canon-new-text"
      />
      <label for="canon-topic" class="hlos-canon__label">
        Tópico (opcional)
      </label>
      <input
        id="canon-topic"
        v-model="newTopic"
        type="text"
        placeholder="ex: derivadas, change management, …"
        data-testid="canon-new-topic"
      />
      <button
        type="submit"
        class="hlos-canon__btn hlos-canon__btn--primary"
        :disabled="!newText.trim()"
        data-testid="canon-submit"
      >
        Guardar
      </button>
    </form>

    <fieldset class="hlos-canon__filter">
      <legend>Filtrar por</legend>
      <div role="radiogroup" aria-label="Filtrar por tipo">
        <label v-for="opt in kindOptions" :key="opt.value" class="hlos-canon__filter-opt">
          <input
            type="radio"
            name="canon-filter"
            :value="opt.value"
            :checked="filter === opt.value"
            @change="filter = opt.value"
          />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </fieldset>

    <ul
      v-if="filtered.length > 0"
      class="hlos-canon__list"
      aria-label="Entradas do Canon"
    >
      <li
        v-for="entry in filtered"
        :key="entry.id"
        class="hlos-canon__entry"
        :data-kind="entry.kind"
      >
        <header class="hlos-canon__entry-head">
          <span class="hlos-canon__entry-kind">{{ entry.kind }}</span>
          <time class="hlos-canon__entry-date">{{ formatDate(entry.createdAt) }}</time>
        </header>
        <p v-if="entry.topic" class="hlos-canon__entry-topic">{{ entry.topic }}</p>
        <p class="hlos-canon__entry-text">{{ entry.text }}</p>
        <button
          type="button"
          class="hlos-canon__entry-delete"
          :aria-label="`Remover entrada de ${entry.kind}`"
          @click="onDelete(entry.id)"
        >
          Remover
        </button>
      </li>
    </ul>
    <p v-else class="hlos-canon__empty">
      Nenhuma entrada ainda. Que tal começar com uma curiosidade do dia?
    </p>
  </section>
</template>

<style scoped>
.hlos-canon {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
}
.hlos-canon__header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
}
.hlos-canon__intro {
  margin: 0 0 1rem;
  color: var(--hlos-color-neutral-600);
  font-size: 0.9em;
}
.hlos-canon__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.hlos-canon__btn {
  font: inherit;
  padding: 0.5rem 1rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.hlos-canon__btn--primary {
  background: var(--hlos-color-primary);
  color: var(--hlos-color-bg);
  border-color: var(--hlos-color-primary);
}
.hlos-canon__btn--tertiary {
  border: none;
  text-decoration: underline;
}
.hlos-canon__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hlos-canon__form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
}
.hlos-canon__label {
  font-weight: 600;
  font-size: 0.9em;
}
.hlos-canon__select,
.hlos-canon__form input,
.hlos-canon__form textarea {
  font: inherit;
  padding: 0.5rem;
  border: 1px solid var(--hlos-color-neutral-300);
  border-radius: 6px;
  background: var(--hlos-color-bg);
  color: inherit;
}
.hlos-canon__prompt {
  margin: 0;
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
  font-style: italic;
}
.hlos-canon__filter {
  border: none;
  padding: 0;
  margin: 1rem 0;
}
.hlos-canon__filter legend {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.hlos-canon__filter-opt {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.75rem;
  font-size: 0.9em;
  cursor: pointer;
}
.hlos-canon__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.hlos-canon__entry {
  padding: 0.75rem;
  border: 1px solid var(--hlos-color-neutral-200);
  border-radius: 8px;
  background: var(--hlos-color-bg);
}
.hlos-canon__entry-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
  margin-bottom: 0.25rem;
}
.hlos-canon__entry-kind {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.75em;
  color: var(--hlos-color-primary);
}
.hlos-canon__entry-topic {
  margin: 0 0 0.25rem;
  font-style: italic;
  font-size: 0.9em;
  color: var(--hlos-color-neutral-600);
}
.hlos-canon__entry-text {
  margin: 0.5rem 0;
  white-space: pre-wrap;
}
.hlos-canon__entry-delete {
  font: inherit;
  font-size: 0.85em;
  border: none;
  background: transparent;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  color: var(--hlos-color-error);
}
.hlos-canon__empty {
  margin: 1rem 0;
  color: var(--hlos-color-neutral-600);
  font-style: italic;
  text-align: center;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
